import { animate, stagger } from "motion";
import { pauseVideosIn } from "../lib/media-crossfade.js";
import { markManifestOverlaySeen } from "../lib/manifest-overlay-storage.js";

const SEGMENT_COUNT = 3;

/** Fraction of the text phase used as a crossfade blend zone around each boundary */
const CROSSFADE_ZONE = 0.05;

/** Scroll distance for each phase, in viewport-height multiples */
const TEXT_VH = 3;
const MEDIA_EXIT_VH = 0.8;

/**
 * Media exit overlap — how early the last video begins scrolling up/out,
 * expressed as a fraction of the text runway. Scales with viewport height
 * so laptops (~900px) feel snappy while large external monitors (~1400px+)
 * don't leave an awkward gap after the text clears.
 */
const MEDIA_EXIT_OVERLAP_MIN = 0.18;
const MEDIA_EXIT_OVERLAP_MAX = 0.35;
const OVERLAP_VP_MIN = 900;
const OVERLAP_VP_MAX = 1400;

/**
 * Lerp factor controlling how quickly the display progress chases real scroll
 * progress each RAF frame. Lower = smoother/slower catch-up; higher = snappier.
 */
const LERP = 0.05;
const VIDEO_LERP = 0.02;
const MAX_DELTA = 0.003;

class ManifestOverlay extends HTMLElement {
  connectedCallback() {
    this.scrollTrack = this.querySelector("[data-manifest-overlay-scroll-track]");
    this.videos = [...this.querySelectorAll("[data-manifest-overlay-video]")];
    this.textTrack = this.querySelector("[data-manifest-overlay-text-track]");
    this.panel = this.querySelector("[data-manifest-overlay-panel]");
    this.mediaContainer = this.querySelector("[data-manifest-overlay-media]");

    this._rafId = null;
    this._completed = false;
    this._entering = true;
    this._loopRunning = false;
    this._displayProgress = 0;
    this._videoProgress = 0;
    this._textRunway = 0;
    this._mediaExitRunway = 0;
    this._totalRunway = 0;
    this._startY = 0;

    this._videoDurations = new Array(SEGMENT_COUNT).fill(0);
    this._videoReady = new Array(SEGMENT_COUNT).fill(false);
    this._segmentThresholds = [0];

    const alwaysShow = this.dataset.alwaysShow === "true";
    const designMode = this.dataset.designMode === "true";

    if (document.documentElement.classList.contains("manifest-overlay-skipped")) {
      this.#hideOverlay();
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.#finishImmediately({ persist: !alwaysShow && !designMode });
      return;
    }

    window.scrollTo(0, 0);

    requestAnimationFrame(() => {
      this.#setupRunway();
      this.#bindVideos();
      this.#playEntrance();
      this.#startLoop();
    });
  }

  disconnectedCallback() {
    this._loopRunning = false;
    cancelAnimationFrame(this._rafId);
    if (this._onResize) window.removeEventListener("resize", this._onResize);
  }

  #hideOverlay() {
    this.classList.add("hidden");
    this.setAttribute("aria-hidden", "true");
    this.style.overflowY = "hidden";
    pauseVideosIn(this);
  }

  #finishImmediately({ persist }) {
    this.#hideOverlay();
    document.documentElement.classList.remove("manifest-overlay-active");
    document.documentElement.classList.add("manifest-overlay-skipped");
    if (persist) markManifestOverlaySeen();
  }

  #setupRunway() {
    const viewport = window.innerHeight;

    this._textRunway = viewport * TEXT_VH;
    this._mediaExitRunway = viewport * MEDIA_EXIT_VH;
    this._totalRunway = this._textRunway + this._mediaExitRunway;

    this._startY = 0;

    // Linearly interpolate exit overlap based on viewport height:
    // small screens (900px) use 0.2, large screens (1400px+) use 0.35
    const t = Math.min(1, Math.max(0, (viewport - OVERLAP_VP_MIN) / (OVERLAP_VP_MAX - OVERLAP_VP_MIN)));
    this._mediaExitOverlap = MEDIA_EXIT_OVERLAP_MIN + t * (MEDIA_EXIT_OVERLAP_MAX - MEDIA_EXIT_OVERLAP_MIN);

    if (this.scrollTrack) {
      this.scrollTrack.style.height = `${this._totalRunway + viewport}px`;
    }

    this.#computeSegmentThresholds();
  }

  #computeSegmentThresholds() {
    if (!this.textTrack) return;

    const segments = this.textTrack.querySelectorAll("[data-manifest-overlay-segment]");
    const trackHeight = this.textTrack.offsetHeight;
    const viewport = window.innerHeight;
    const totalScroll = trackHeight + viewport * 0.15;
    const viewportPadding = window.innerWidth >= 768 ? 216 : 205;
    const triggerY = viewport * 0.4;

    if (totalScroll <= 0 || segments.length === 0) {
      this._segmentThresholds = [0];
      return;
    }

    this._segmentThresholds = [...segments].map((seg) => {
      const t = (viewportPadding + seg.offsetTop - triggerY) / totalScroll;
      return Math.max(0, Math.min(1, t));
    });
  }

  #playEntrance() {
    if (!this.textTrack) return;

    if (this.mediaContainer) {
      requestAnimationFrame(() => {
        this.mediaContainer.style.opacity = "1";
      });
    }

    const heading = this.textTrack.querySelector("h3");
    const segments = [...this.textTrack.querySelectorAll("[data-manifest-overlay-segment]")];

    this.textTrack.style.transform = `translateY(${this._startY}px)`;
    this.textTrack.style.opacity = "1";

    const offscreen = { transform: "translateY(60px)", opacity: "0" };
    if (heading) {
      heading.style.transform = offscreen.transform;
      heading.style.opacity = offscreen.opacity;
    }
    segments.forEach((seg) => {
      seg.style.transform = offscreen.transform;
      seg.style.opacity = offscreen.opacity;
    });

    const opts = {
      duration: 0.6,
      type: "spring",
      stiffness: 100,
      damping: 20,
      mass: 1,
      delay: stagger(0.18, { startDelay: 0.5 })
    };
    const to = { transform: "translateY(0px)", opacity: 1 };

    const elements = [heading, ...segments].filter(Boolean);
    animate(elements, to, opts).finished.then(() => {
      this._entering = false;
    });
  }

  #bindVideos() {
    this.videos.forEach((video, i) => {
      const onMeta = () => {
        this._videoDurations[i] = video.duration;
        this._videoReady[i] = Number.isFinite(video.duration) && video.duration > 0;
        video
          .play()
          .then(() => video.pause())
          .catch(() => {});
      };

      if (video.readyState >= 1) {
        onMeta();
      } else {
        video.addEventListener("loadedmetadata", onMeta, { once: true });
      }
    });
  }

  #startLoop() {
    this._loopRunning = true;

    this._onResize = () => this.#setupRunway();
    window.addEventListener("resize", this._onResize, { passive: true });

    const loop = () => {
      if (!this._loopRunning) return;
      this._rafId = requestAnimationFrame(loop);
      this.#onFrame();
    };
    this._rafId = requestAnimationFrame(loop);
  }

  getProgress() {
    if (this._totalRunway <= 0) return 0;
    return Math.min(1, this.scrollTop / this._totalRunway);
  }

  #onFrame() {
    if (this._completed) return;

    const targetProgress = this.getProgress();
    const textDelta = (targetProgress - this._displayProgress) * LERP;
    this._displayProgress += Math.sign(textDelta) * Math.min(Math.abs(textDelta), MAX_DELTA);

    const scrolled = this._displayProgress * this._totalRunway;
    const progress = this._displayProgress;

    this.dataset.progress = progress.toFixed(3);

    // --- Text phase ---
    let textPhase = 0;
    let textOpacity = 1;

    if (scrolled < this._textRunway) {
      textPhase = this._textRunway > 0 ? scrolled / this._textRunway : 0;
      textOpacity = textPhase > 0.8 ? 1 - (textPhase - 0.8) / 0.2 : 1;
    } else {
      textPhase = 1;
      textOpacity = 0;
    }

    if (this.textTrack && !this._entering) {
      const viewport = window.innerHeight;
      const trackHeight = this.textTrack.offsetHeight;
      const endY = -(trackHeight + viewport * 0.15);
      const y = this._startY + (endY - this._startY) * textPhase;
      this.textTrack.style.transform = `translateY(${y}px)`;
      this.textTrack.style.opacity = String(textOpacity);
    }

    // --- Video segment crossfade (scroll-driven blend zone) ---
    this.#blendVideos(textPhase);

    // --- Video scrubbing ---
    const videoDelta = (targetProgress - this._videoProgress) * VIDEO_LERP;
    this._videoProgress += Math.sign(videoDelta) * Math.min(Math.abs(videoDelta), MAX_DELTA);
    const videoEndProgress = TEXT_VH / (TEXT_VH + MEDIA_EXIT_VH);
    const videoPhase = Math.min(1, this._videoProgress / videoEndProgress);
    const videoSegIndex = this.#getSegmentForPhase(videoPhase);
    const videoSegLocal = this.#getSegmentLocalProgress(videoPhase, videoSegIndex);

    const activeVideo = this.videos[videoSegIndex];
    if (activeVideo && this._videoReady[videoSegIndex]) {
      const targetTime = videoSegLocal * this._videoDurations[videoSegIndex];
      if (Math.abs(targetTime - activeVideo.currentTime) > 0.04) {
        activeVideo.currentTime = targetTime;
      }
    }

    // --- Media exit: begins during the last portion of the text phase ---
    const mediaExitStart = this._textRunway * (1 - this._mediaExitOverlap);
    const mediaExitRange = this._mediaExitRunway + this._textRunway * this._mediaExitOverlap;

    if (this.mediaContainer) {
      let mediaExitPhase = 0;
      if (scrolled > mediaExitStart && mediaExitRange > 0) {
        mediaExitPhase = Math.min(1, (scrolled - mediaExitStart) / mediaExitRange);
      }
      const mediaY = -(window.innerHeight * 0.6 * mediaExitPhase);
      const mediaOpacity = 1 - mediaExitPhase;
      this.mediaContainer.style.transform = `translateY(${mediaY}px)`;
      this.mediaContainer.style.opacity = String(mediaOpacity);
    }

    // Overlay fade — whole overlay fades out during media-exit phase
    if (scrolled >= mediaExitStart) {
      document.documentElement.classList.remove("manifest-overlay-active");
      const fadeProgress = mediaExitRange > 0 ? (scrolled - mediaExitStart) / mediaExitRange : 1;
      this.style.opacity = String(1 - Math.min(1, fadeProgress));
    }

    if (progress >= 0.95 && !this._entranceFired) {
      this._entranceFired = true;
      window.dispatchEvent(new CustomEvent("manifest-overlay:complete"));
    }

    if (progress >= 0.999) {
      this.#complete();
    }
  }

  #getSegmentForPhase(phase) {
    const thresholds = this._segmentThresholds;
    if (!thresholds || thresholds.length === 0) return 0;

    let index = 0;
    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (phase >= thresholds[i]) {
        index = i;
        break;
      }
    }
    return Math.min(index, SEGMENT_COUNT - 1);
  }

  #getSegmentLocalProgress(phase, segIndex) {
    const thresholds = this._segmentThresholds;
    if (!thresholds || thresholds.length === 0) return 0;

    const start = thresholds[segIndex] || 0;
    const end = thresholds[segIndex + 1] || 1;
    const range = end - start;
    if (range <= 0) return 0;
    return Math.max(Math.min(1, (phase - start) / range));
  }

  #blendVideos(phase) {
    const thresholds = this._segmentThresholds;
    if (!thresholds || this.videos.length === 0) return;

    for (let i = 0; i < this.videos.length; i++) {
      const start = thresholds[i] || 0;
      const end = thresholds[i + 1] || 1;

      let opacity;
      if (phase < start - CROSSFADE_ZONE) {
        opacity = 0;
      } else if (phase < start) {
        opacity = (phase - (start - CROSSFADE_ZONE)) / CROSSFADE_ZONE;
      } else if (phase < end - CROSSFADE_ZONE) {
        opacity = 1;
      } else if (phase < end) {
        opacity = 1 - (phase - (end - CROSSFADE_ZONE)) / CROSSFADE_ZONE;
      } else {
        opacity = 0;
      }

      if (i === 0 && phase < start) opacity = 1;
      if (i === this.videos.length - 1 && phase >= end - CROSSFADE_ZONE) opacity = 1;

      this.videos[i].style.opacity = String(Math.max(0, Math.min(1, opacity)));
    }
  }

  #complete() {
    if (this._completed) return;
    this._completed = true;
    this._loopRunning = false;

    const persist = this.dataset.alwaysShow !== "true" && this.dataset.designMode !== "true" && !import.meta.env.DEV;
    if (persist) markManifestOverlaySeen();

    document.documentElement.classList.remove("manifest-overlay-active");
    document.documentElement.classList.add("manifest-overlay-skipped");

    window.scrollTo({ top: 0, behavior: "instant" });

    this.style.pointerEvents = "none";
    this.#hideOverlay();
  }
}

if (!customElements.get("manifest-overlay")) {
  customElements.define("manifest-overlay", ManifestOverlay);
}
